<template>
  <div class="d-flex justify-start align-center pt-6">
    <ConfirmRegisterDialog
      :state="confirmStudentDialog"
      :importedDocuments="importedDocuments"
      @closeDialog="closeConfirmDialog"
      @updateResult="onUpdateResultDocument"
      ref="registerDialog"
    />
    <InvalidCodeDialog
      :state="invalidDialog"
      :invalidCode="invalidCode"
      :invalidExamCode="invalidExamCode"
      :invalidSubjects="invalidSubjects"
      @closeDialog="closeInvalid"
    />
    <v-menu open-on-hover bottom offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn class="mt-0 ml-6" color="green" dark v-bind="attrs" v-on="on" outlined>
          Nhập/xuất Excel học sinh
        </v-btn>
      </template>

      <v-list>
        <v-list-item class="cursor-pointer">
          <v-list-item-title @click="onUploadClick">Tải lên tệp Excel chứa thông tin học sinh</v-list-item-title>
          <input
            ref="uploader"
            type="file"
            class="d-none"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            @change="onFileChanged"
          />
        </v-list-item>
        <v-list-item class="cursor-pointer">
          <v-list-item-title @click="exportDataToExcel">Tải xuống tệp Excel</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import { mapActions, mapGetters } from 'vuex'
import readXlsxFile from 'read-excel-file'
import { StudentExam } from '@/plugins/api'
import ConfirmRegisterDialog from './ConfirmRegisterDialog.vue'
import InvalidCodeDialog from './InvalidCodeDialog.vue'
import moment from 'moment'
import XLSX from 'xlsx-js-style'

const schema = {
  'Mã định danh Bộ GD&ĐT': {
    prop: 'code_short',
    type: String,
    required: true
  },
  'Họ tên': {
    prop: 'name',
    type: String
  },
  'Số báo danh': {
    prop: 'examCode',
    type: String
  },
  'Tổ hợp thi': {
    prop: 'subjectGroup',
    type: String
  }
}

export default {
  components: { ConfirmRegisterDialog, InvalidCodeDialog },
  props: {
    exam: String,
    query: Object
  },
  data() {
    return {
      dialog: false,
      students: null,
      confirmStudentDialog: false,
      isSelecting: false,
      invalidDialog: false,
      invalidCode: [],
      invalidExamCode: [],
      invalidSubjects: [],
      importedDocuments: [],
      json_fields: {
        STT: 'index',
        'Mã định danh Bộ GD&ĐT': 'student.code_short',
        'Số báo danh': 'examCode',
        'Họ tên': 'student.name',
        'Ngày sinh': 'student.dob',
        'Giới tính': 'student.gender',
        Lớp: 'class.title',
        'Phòng thi': 'room',
        'Mã đề': 'note',
        'Số tờ': 'note2',
        'Kí tên': 'sign',
        'Tổ hợp thi': 'subjectGroup.name'
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['user', 'isAuthenticated'])
  },
  methods: {
    ...mapActions('studentExam', ['fetchStudentExams']),
    onUploadClick() {
      this.isSelecting = true
      window.addEventListener(
        'focus',
        () => {
          this.isSelecting = false
        },
        { once: true }
      )
      this.$refs.uploader.click()
    },

    async getStudentExcel() {
      this.$loading.active = true
      const students = await this.fetchStudentExams({
        exam: this.exam,
        ...this.query,
        _limit: -1,
        _sort: 'examCode:asc'
      })
      this.students = students
      this.$loading.active = false
    },
    async onFileChanged(e) {
      this.$loading.active = true
      this.selectedFile = e.target.files[0]
      let readFileResult
      console.log(this.selectedFile)
      try {
        readFileResult = await readXlsxFile(this.selectedFile, {
          schema,
          transformData(rows) {
            return rows.slice(7)
          }
        })
        console.log(readFileResult)
      } catch (error) {
        console.error(error)
        this.$alert.error('File không có định dạng .xlsx hoặc có dữ liệu không hợp lệ (tham khảo mẫu file)')
        this.$loading.active = false
        return
      }

      this.$alert.success('Đã đọc File thành công!')

      if (readFileResult.errors.length !== 0) {
        const error = readFileResult.errors[0]
        this.$alert.error(`Lỗi Dòng ${error.row} - Cột ${error.column} - ${error.value}: ${error.error}`)
        this.$loading.active = false
        return
      } else {
        const data = readFileResult.rows
        this.importedDocuments = data
        this.confirmStudentDialog = true
      }

      this.$loading.active = false
    },
    formatDataForExport(data) {
      return data.map((item, index) => {
        let formattedItem = {}
        for (let key in this.json_fields) {
          const fieldPath = this.json_fields[key]
          let value = item

          // Traverse the field path to get the value
          if (typeof fieldPath === 'string') {
            const pathParts = fieldPath.split('.')
            pathParts.forEach(part => {
              value = value ? value[part] : '' // Set value to '' if it's undefined/null
            })
          }

          // Handle date formatting
          if ((key === 'Ngày sinh' || key === 'Ngày tạo') && value) {
            value = moment(value).format('DD/MM/YYYY')
          }

          // Handle gender formatting
          if (key === 'Giới tính' && value) {
            value = value === 'male' ? 'Nam' : 'Nữ'
          }

          // Add the STT value based on the index
          if (key === 'STT') {
            value = index + 1 // STT starts from 1
          }

          formattedItem[key] = value !== undefined && value !== null ? value : ''

          // Handle special case for 'Số báo danh'
          if (key === 'Số báo danh' && value) {
            formattedItem[key] = { v: value, t: 's' }
          }
        }
        return formattedItem
      })
    },

    async exportDataToExcel() {
      await this.getStudentExcel()
      const formattedData = this.formatDataForExport(this.students)

      const dataForSheet =
        formattedData.length > 0 ? formattedData : this.getHeaders().map(header => ({ [header]: '' }))

      const worksheet = XLSX.utils.json_to_sheet([], { skipHeader: true })

      const headers = [
        ['SỞ GIÁO DỤC VÀ ĐÀO TẠO HÀ NỘI', ''], // Header 1
        ['TRƯỜNG: TH Lý Thái Tổ', ''], // Header 2
        ['', ''],
        ['DANH SÁCH HỌC SINH KIỂM TRA', ''], // Header 4
        ['NĂM HỌC 2024 - 2025', ''], // Header 5
        ['', ''],
        ['', '']
      ]

      // Add headers with styles
      headers.forEach((header, rowIndex) => {
        const cellAddress = `A${rowIndex + 1}`
        worksheet[cellAddress] = { v: header[0], t: 's' }

        if (rowIndex === 0 || rowIndex === 1) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 13, name: 'Times New Roman' },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        }

        if (rowIndex === 3 || rowIndex === 4) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 17, name: 'Times New Roman' },
            alignment: { horizontal: 'center', vertical: 'center' }
          }
        }
      })

      // Define merges for the headers
      worksheet['!merges'] = [
        { s: { r: 3, c: 0 }, e: { r: 3, c: 13 } },
        { s: { r: 4, c: 0 }, e: { r: 4, c: 13 } },
        { s: { r: 5, c: 0 }, e: { r: 5, c: 13 } }
      ]

      // Define common styles for headers and data cells
      const headerStyle = {
        font: { bold: true, sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }

      const dataStyle = {
        font: { sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }

      // Add the actual table data starting from A8
      XLSX.utils.sheet_add_json(worksheet, dataForSheet, {
        origin: 'A8',
        header: this.getHeaders(),
        skipHeader: false
      })

      // Apply styles to the headers (row 8)
      this.getHeaders().forEach((header, colIndex) => {
        const colLetter = String.fromCharCode(65 + colIndex) // Convert column index to letter (A, B, C, ...)
        const headerCellAddress = `${colLetter}8`

        // Ensure the header cell exists before applying styles
        if (worksheet[headerCellAddress]) {
          worksheet[headerCellAddress].s = headerStyle // Apply header style
        }

        // Apply data style to each data row only if data is available
        if (formattedData.length > 0) {
          for (let rowIndex = 9; rowIndex < dataForSheet.length + 9; rowIndex++) {
            const dataCellAddress = `${colLetter}${rowIndex}`

            // Ensure the data cell exists before applying styles
            if (worksheet[dataCellAddress]) {
              worksheet[dataCellAddress].s = dataStyle
            }
          }
        }
      })

      // If there is no data, apply the border to just the first row after headers
      if (formattedData.length === 0) {
        this.getHeaders().forEach((header, colIndex) => {
          const colLetter = String.fromCharCode(65 + colIndex) // Convert column index to letter (A, B, C, ...)
          const dataCellAddress = `${colLetter}9`

          worksheet[dataCellAddress] = { v: '', t: 's' }
          worksheet[dataCellAddress].s = headerStyle // Apply header style to the first row after headers
        })
      }

      this.setColumnWidths(worksheet, dataForSheet)

      // Create a new workbook and append the worksheet
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Học sinh')

      // Export the file
      XLSX.writeFile(workbook, 'hoc-sinh-dang-ky-mau.xlsx')
      this.$loading.active = false
    },
    getHeaders() {
      const headers = []
      for (let key in this.json_fields) {
        headers.push(key)
      }
      return headers
    },
    async onUpdateResultDocument(data) {
      this.$refs.uploader.value = null
      const fileData = {
        exam: this.exam,
        register: data
      }
      const assigned = await StudentExam.fileRegister(fileData)
      if (!assigned.invalidCode || !assigned.invalidExamCode) {
        this.closeConfirmDialog()
        this.$emit('refresh')
      } else {
        this.$refs.registerDialog.stopLoading()
        this.invalidCode = assigned.invalidCode
        this.invalidExamCode = assigned.invalidExamCode
        this.invalidSubjects = assigned.missingSubjects
        this.invalidDialog = true
      }
    },
    closeInvalid() {
      this.invalidDialog = false
      this.invalidRooms = ''
    },
    setColumnWidths(worksheet, dataForSheet) {
      const columnWidths = this.getHeaders().map((header, colIndex) => {
        const maxContentLength = Math.max(
          header.length,
          ...dataForSheet.map(row => (row[header] ? row[header].toString().length : 0))
        )
        return { wch: Math.max(10, maxContentLength + 2) }
      })

      worksheet['!cols'] = columnWidths
    },
    closeConfirmDialog() {
      this.confirmStudentDialog = false
      this.$refs.uploader.value = null
    }
  }
}
</script>
